import { ViewBooking } from "collins-js-sdk";
import { BookableArea } from "../../types/venues";
import { parseTimeToMinutes } from "./timeUtils";

export interface SuboptimalUsageResult {
  isSuboptimal: boolean;
  bookingCount: number;
  description: string;
}

interface FlaggedBooking {
  bookingId: string;
  reason: string;
}

const PARTY_TO_CAPACITY_THRESHOLD = 0.35;
const MIN_FLAGGED_BOOKING_COUNT = 3;

/**
 * Detects suboptimal table usage across bookings.
 * All detection is client-side with no API calls.
 * Only flags as suboptimal when bookingCount >= 3 to avoid noise.
 */
export const detectSuboptimalUsage = (
  bookings: ViewBooking[],
  areas: BookableArea[],
  date?: Date
): SuboptimalUsageResult => {
  if (!bookings || bookings.length === 0 || !areas || areas.length === 0) {
    return { isSuboptimal: false, bookingCount: 0, description: "" };
  }

  const areaMap = buildAreaMap(areas);
  const flagged: FlaggedBooking[] = [];

  const activeBookings = getActiveBookings(bookings, date);

  for (const booking of activeBookings) {
    if (isLargeTableSmallParty(booking, areaMap)) {
      flagged.push({
        bookingId: booking.id ?? "",
        reason: "large_table_small_party"
      });
      continue;
    }

    if (isUnassignedNeedingTable(booking)) {
      flagged.push({
        bookingId: booking.id ?? "",
        reason: "unassigned_needs_table"
      });
    }
  }

  const timeSlotIssues = findTimeSlotMismatches(activeBookings, areaMap);
  for (const issue of timeSlotIssues) {
    if (!flagged.some((f) => f.bookingId === issue.bookingId)) {
      flagged.push(issue);
    }
  }

  const bookingCount = flagged.length;

  if (bookingCount < MIN_FLAGGED_BOOKING_COUNT) {
    return { isSuboptimal: false, bookingCount: 0, description: "" };
  }

  return {
    isSuboptimal: true,
    bookingCount,
    description: buildDescription(flagged)
  };
};

/**
 * Builds a lookup map from area ID to BookableArea for efficient access.
 */
const buildAreaMap = (areas: BookableArea[]): Map<string, BookableArea> => {
  const map = new Map<string, BookableArea>();
  for (const area of areas) {
    map.set(area.id, area);
  }
  return map;
};

const EXCLUDED_STATUSES = new Set(["rejected", "deleted", "lost"]);

const STARTED_STAGES = new Set([
  "arrived",
  "seated",
  "dessert",
  "left",
  "bill"
]);

/**
 * Filters to bookings that the server-side optimiser can actually move.
 * Excludes rejected/deleted/lost statuses plus bookings the optimiser
 * would skip: started, locked, guestlist, waitlisted and those with
 * deposits or pre-orders (which cause server-side locking).
 */
const getActiveBookings = (
  bookings: ViewBooking[],
  date?: Date
): ViewBooking[] => {
  const now = new Date();
  const isToday =
    date != null &&
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  const isPast =
    date != null &&
    !isToday &&
    date.getTime() <
      new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

  if (isPast) return [];

  const currentMinutes = isToday ? now.getHours() * 60 + now.getMinutes() : -1;

  return bookings.filter((b) => {
    if (EXCLUDED_STATUSES.has(b.status ?? "")) return false;
    if (STARTED_STAGES.has(b.currentStage ?? "")) return false;
    if (b.assignedAreaLocked) return false;
    if (b.type?.guestlist) return false;
    if (b.waitlisted && b.status === "in_progress") return false;
    if (b.deposits && b.deposits.length > 0) return false;
    if (b.hasPreorders) return false;
    if (isToday && currentMinutes >= 0) {
      const bookingMinutes = parseTimeToMinutes(b.time ?? "");
      if (bookingMinutes >= 0 && bookingMinutes < currentMinutes) return false;
    }
    return true;
  });
};

/**
 * Checks if a booking's party size is <= 35% of the assigned table's capacity.
 */
const isLargeTableSmallParty = (
  booking: ViewBooking,
  areaMap: Map<string, BookableArea>
): boolean => {
  const partySize = booking.numPeople;
  const assignedAreas = booking.assignedAreas;

  if (!partySize || !assignedAreas || assignedAreas.length === 0) {
    return false;
  }

  return assignedAreas.some((assigned) => {
    const areaId = String(assigned.id ?? "");
    const area = areaMap.get(areaId);
    if (!area) return false;

    const capacity = area.max_people ?? area.max_people_seated ?? 0;
    if (capacity === 0) return false;

    return partySize <= capacity * PARTY_TO_CAPACITY_THRESHOLD;
  });
};

/**
 * Checks if a booking is unassigned but has a status indicating it needs a table.
 */
const isUnassignedNeedingTable = (booking: ViewBooking): boolean => {
  if (booking.status === "in_progress") return false;
  const hasNoAssignment =
    !booking.assignedAreas || booking.assignedAreas.length === 0;

  return hasNoAssignment;
};

/**
 * Finds bookings on large tables in the same time slot when smaller tables
 * are available. A booking is flagged if it occupies a table larger than
 * needed and a smaller suitable table exists that is not in use during
 * the same time window.
 */
const findTimeSlotMismatches = (
  bookings: ViewBooking[],
  areaMap: Map<string, BookableArea>
): FlaggedBooking[] => {
  const flagged: FlaggedBooking[] = [];

  const assignedBookings = bookings.filter(
    (b) => b.assignedAreas && b.assignedAreas.length > 0 && b.numPeople
  );

  const occupiedSlots = buildOccupiedSlots(assignedBookings);

  for (const booking of assignedBookings) {
    const partySize = booking.numPeople ?? 0;
    if (partySize === 0) continue;

    const assignedAreaId = String(booking.assignedAreas?.[0]?.id ?? "");
    const assignedArea = areaMap.get(assignedAreaId);
    if (!assignedArea) continue;

    const assignedCapacity =
      assignedArea.max_people ?? assignedArea.max_people_seated ?? 0;
    if (
      assignedCapacity === 0 ||
      partySize > assignedCapacity * PARTY_TO_CAPACITY_THRESHOLD
    )
      continue;

    const bookingStart = parseTimeToMinutes(booking.time ?? "");
    const bookingEnd = bookingStart + (booking.duration ?? 0);
    if (bookingStart < 0 || bookingEnd <= bookingStart) continue;

    const hasSmallerAvailable = Array.from(areaMap.values()).some((area) => {
      const capacity = area.max_people ?? area.max_people_seated ?? 0;
      if (capacity >= assignedCapacity || capacity < partySize) return false;

      return !isAreaOccupied(
        area.id,
        bookingStart,
        bookingEnd,
        occupiedSlots,
        booking.id
      );
    });

    if (hasSmallerAvailable) {
      flagged.push({
        bookingId: booking.id ?? "",
        reason: "smaller_table_available"
      });
    }
  }

  return flagged;
};

interface OccupiedSlot {
  bookingId: string;
  areaId: string;
  start: number;
  end: number;
}

/**
 * Builds a list of occupied time slots from assigned bookings.
 */
const buildOccupiedSlots = (bookings: ViewBooking[]): OccupiedSlot[] => {
  const slots: OccupiedSlot[] = [];

  for (const booking of bookings) {
    const start = parseTimeToMinutes(booking.time ?? "");
    const end = start + (booking.duration ?? 0);
    if (start < 0 || end <= start) continue;

    for (const area of booking.assignedAreas ?? []) {
      slots.push({
        bookingId: booking.id ?? "",
        areaId: String(area.id ?? ""),
        start,
        end
      });
    }
  }

  return slots;
};

/**
 * Checks whether an area is occupied during a given time window,
 * excluding the specified booking.
 */
const isAreaOccupied = (
  areaId: string,
  start: number,
  end: number,
  slots: OccupiedSlot[],
  excludeBookingId?: string
): boolean =>
  slots.some(
    (slot) =>
      slot.areaId === areaId &&
      slot.bookingId !== excludeBookingId &&
      slot.start < end &&
      slot.end > start
  );

/**
 * Builds a human-readable description from flagged bookings.
 */
const buildDescription = (flagged: FlaggedBooking[]): string => {
  const reasons = new Map<string, number>();
  for (const f of flagged) {
    reasons.set(f.reason, (reasons.get(f.reason) ?? 0) + 1);
  }

  const parts: string[] = [];

  const largeTable = reasons.get("large_table_small_party") ?? 0;
  if (largeTable > 0) {
    parts.push(
      `${largeTable} booking${largeTable !== 1 ? "s on" : " on"} oversized tables`
    );
  }

  const unassigned = reasons.get("unassigned_needs_table") ?? 0;
  if (unassigned > 0) {
    parts.push(
      `${unassigned} unassigned booking${unassigned !== 1 ? "s" : ""}`
    );
  }

  const smallerAvailable = reasons.get("smaller_table_available") ?? 0;
  if (smallerAvailable > 0) {
    parts.push(
      `${smallerAvailable} booking${smallerAvailable !== 1 ? "s" : ""} with better-fitting tables available`
    );
  }

  return parts.join(", ");
};
